/* eslint-disable no-extend-native */
import React, { useEffect, useState } from "react";
import styles from "./ProductViews.module.sass";
import cn from "classnames";
import Card from "../../../components/Card";
import Dropdown from "../../../components/Dropdown";
import { UseApi } from "../../../hooks/use-api";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	Legend,
} from "recharts";
import useDarkMode from "use-dark-mode";
import Loader from "../../../components/Loader";
import { GetActivityCount } from "../../../Services/lib/nft";
import home from "../../../i11/Home";
import { useUser } from "../../../context";

const intervals = ["last_7_days", "last_14_days", "last_month", "last_year"];
const intervalVals = {
	last_7_days: 6,
	last_14_days: 13,
	last_month: 29,
	last_year: 364,
};
const options = ["all", "direct_purchase", "community_market"];
const optionsVal = {
	all: undefined,
	direct_purchase: "PRIMARY",
	community_market: "SECONDARY",
};

const dateOptions = { month: "numeric", day: "numeric" };
const dateOptions_ = { month: "numeric", year: "numeric" };

Date.isLeapYear = function (year) {
	return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
};

Date.prototype.isLeapYear = function () {
	var y = this.getFullYear();
	return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
};

Date.getDaysInMonth = function (year, month) {
	return [
		31,
		Date.isLeapYear(year) ? 29 : 28,
		31,
		30,
		31,
		30,
		31,
		31,
		30,
		31,
		30,
		31,
	][month];
};

Date.prototype.getDaysInMonth = function () {
	return Date.getDaysInMonth(this.getFullYear(), this.getMonth());
};

Date.prototype.addMonths = function (value) {
	var n = this.getDate();
	this.setDate(1);
	this.setMonth(this.getMonth() + value);
	this.setDate(Math.min(n, this.getDaysInMonth()));
	return this;
};

const ProductViews = ({ className }) => {
	const { locale } = useUser();
	const darkMode = useDarkMode(false);
	const [sorting, setSorting] = useState(intervals[0]);
	const [option, setOption] = useState(options[0]);
	const [data, setData] = useState([]);
	const [{ isLoading }, fetch] = UseApi({
		service: GetActivityCount,
	});

	const getCount = async () => {
		const res = await fetch({
			range: intervalVals[sorting],
			market: optionsVal[option],
		});

		let cnts = {};
		if (res) {
			for (
				let index = intervalVals[sorting] === 364 ? 11 : intervalVals[sorting];
				index >= 0;
				index--
			) {
				const date = new Date();
				if (intervalVals[sorting] === 364) {
					date.addMonths(-index);
				} else {
					date.setDate(date.getDate() - index);
				}

				cnts[
					intervalVals[sorting] === 364
						? date
								.toLocaleDateString("en-US", dateOptions_)
								.split("/")
								.map((e, i) => {
									if (i === 0) return e;
									else return e.slice(2);
								})
								.join("/")
						: date.toLocaleDateString("en-US", dateOptions)
				] = {
					cnt: 0,
					index,
					PRIMARY: 0,
					SECONDARY: 0,
				};
			}
			res.forEach((e) => {
				const key = e._id.createdAt
					.split("/")
					.map((e, i) => {
						if (intervalVals[sorting] === 364) {
							if (i === 0) {
								if (e[0] === "0") return e[1];
								else return e;
							} else {
								return e.slice(2);
							}
						} else if (e[0] === "0") return e[1];
						else return e;
					})
					.join("/");
				cnts[key].cnt += e.count;
				cnts[key][e._id.market] += e.count;
			});
		}
		if (!optionsVal[option]) {
			setData(
				Object.keys(cnts)
					.map((e) => {
						return {
							name: e,
							[rev.PRIMARY]: cnts[e].PRIMARY,
							[rev.SECONDARY]: cnts[e].SECONDARY,
							index: cnts[e].index,
						};
					})
					.sort((a, b) => b.index - a.index)
			);
		} else {
			setData(
				Object.keys(cnts)
					.map((e) => {
						return {
							name: e,
							[home[locale].purchases]: cnts[e].cnt,
							index: cnts[e].index,
						};
					})
					.sort((a, b) => b.index - a.index)
			);
		}
	};

	useEffect(() => {
		getCount();
	}, [option, sorting]);

	const rev = {
		PRIMARY: home[locale].optionsDic.direct_purchase.title,
		SECONDARY: home[locale].optionsDic.community_market.title,
	};
	return (
		<Card
			className={cn(styles.card, className)}
			title={home[locale].product_purchases}
			classTitle="title-purple"
			classCardHead={styles.viewHead}
			head={
				<>
					<Dropdown
						className={styles.dropdown}
						classDropdownHead={styles.dropdownHead}
						value={sorting}
						setValue={setSorting}
						options={intervals}
						dictionary={home[locale].intervalsDic}
						small
					/>
					<Dropdown
						className={styles.dropdown1}
						classDropdownHead={styles.dropdownHead}
						value={option}
						setValue={setOption}
						options={options}
						dictionary={home[locale].optionsDic}
						small
					/>
				</>
			}
		>
			<div className={styles.chart}>
				<ResponsiveContainer width="100%" height="100%">
					{isLoading ? (
						<div className={styles.loaderWrap}>
							<Loader />
						</div>
					) : (
						<BarChart
							width={500}
							height={300}
							data={data}
							margin={{
								top: 0,
								right: 0,
								left: 0,
								bottom: 0,
							}}
							barSize={32}
							barGap={8}
						>
							<CartesianGrid
								strokeDasharray="none"
								stroke={darkMode.value ? "#272B30" : "#EFEFEF"}
								vertical={false}
							/>
							<XAxis
								dataKey="name"
								axisLine={false}
								tickLine={false}
								tick={{ fontSize: 12, fontWeight: "500", fill: "#6F767E" }}
								padding={{ left: 10 }}
							/>
							<YAxis
								axisLine={false}
								tickLine={false}
								tick={{ fontSize: 12, fontWeight: "500", fill: "#6F767E" }}
							/>
							<Tooltip
								contentStyle={{
									backgroundColor: "#272B30",
									borderColor: "rgba(255, 255, 255, 0.12)",
									borderRadius: 8,
									boxShadow:
										"0px 4px 8px rgba(0, 0, 0, 0.1), 0px 2px 4px rgba(0, 0, 0, 0.1), inset 0px 0px 1px #000000",
								}}
								labelStyle={{ fontSize: 12, fontWeight: "500", color: "#fff" }}
								itemStyle={{
									padding: 0,
									textTransform: "capitalize",
									fontSize: 12,
									fontWeight: "600",
									color: "#fff",
								}}
								cursor={{ fill: "#f3f2f3" }}
							/>
							{!optionsVal[option] ? (
								<>
									<Bar
										dataKey={home[locale].optionsDic.direct_purchase.title}
										fill="#2A85FF"
									/>
									<Bar
										dataKey={home[locale].optionsDic.community_market.title}
										fill="#FFBC99"
									/>
								</>
							) : (
								<Bar dataKey={home[locale].purchases} fill="#B5E4CA" />
							)}
							<Legend />
						</BarChart>
					)}
				</ResponsiveContainer>
			</div>
		</Card>
	);
};

export default ProductViews;
